import React, { useState } from "react";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  CssBaseline,
  styled,
  Avatar,
  Tooltip,
  IconButton,
  Button,
  Typography,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
} from "@mui/icons-material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logoT from "../../Assets/logo.png";
import logo from "../../Assets/TS Logo-1.png";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import {
  LogoutSvgIcon,
  SupportAndQueris,
  AccountSettings,
  AdminControlSvgIcon,
  PaymentDetailsSvgIcon,
  CategoryCreationSvgIcon,
  DosandDontsIcon,
} from "../../Assets/Icon_Svg_Format/Admin_Icon_Svg_Format";
import { useLogout } from "../../logout/Logout";

const drawerWidth = 275;
const collapsedWidth = 70;

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(0, 1.5),
  ...theme.mixins.toolbar,
  minHeight: "64px",
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const SidebarContent = ({ drawerOpen, setDrawerOpen, theme }) => {
  const location = useLocation();
  const [arrow, setArrow] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [activePath, setActivePath] = useState(location.pathname);

  const NAVIGATION = [
    { kind: "header", title: "Introduction" },
    {
      text: "Release Notes",
      icon: <DosandDontsIcon size={29} />,
      path: "/superAdmin/ReleaseNotes",
    },
    { kind: "header", title: "Admin Management" },
    {
      text: "Admin Controls",
      icon: <AdminControlSvgIcon size={29} />,
      path: "/superAdmin/adminControls",
    },
    {
      text: "Payment Details",
      icon: <PaymentDetailsSvgIcon size={25} />,
      path: "/superAdmin/paymentDetails",
    },
    { kind: "header", title: "Category" },
    {
      text: "Category Creation",
      icon: <CategoryCreationSvgIcon size={25} />,
      path: "/superAdmin/category",
    },
    // { kind: "divider" },
    { kind: "header", title: "Support" },
    {
      text: "Support and Queries",
      icon: <SupportAndQueris size={26} />,
      path: "/superAdmin/support",
    },
    {
      text: "Account Settings",
      icon: <AccountSettings size={28} />,
      path: "/superAdmin/accountSettings",
    },
  ];

  const handleMenuClick = (path) => setActivePath(path);
  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);
  const handleSettingsClick = () => {};
  const handleChangePassword = () => setPasswordDialog(true);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: "hidden",
        }}
      >
        <DrawerHeader>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {drawerOpen ? (
              <Box
                component="img"
                src={logoT}
                alt="Company Logo"
                sx={{
                  height: 40,
                  mt: 2,
                  objectFit: "contain",
                  transition: "opacity 0.3s ease",
                }}
              />
            ) : (
              <Avatar
                src={logo}
                variant="rounded"
                sx={{
                  width: 40,
                  height: 36,
                  borderRadius: "8px",
                  backgroundColor: theme.palette.background.default,
                }}
              />
            )}
          </Box>
        </DrawerHeader>

        {/* <Divider /> */}
        <List sx={{ pr: 0.1 }}>
          {NAVIGATION.map((item, index) => {
            if (item.kind === "divider")
              return <Divider key={`divider-${index}`} sx={{ my: 1 }} />;
            if (item.kind === "header")
              return drawerOpen ? (
                <ListItem
                  key={`header-${index}`}
                  sx={{
                    py: 1,
                    px: 2,
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  {item.title}
                </ListItem>
              ) : null;

            const individualProfile = "/superAdmin/adminControls/profile";

            const isActive =
              location.pathname === item.path ||
              (location.pathname === individualProfile &&
                item.path === "/superAdmin/adminControls");

            return (
              <ListItem
                key={item.text}
                disablePadding
                sx={{ display: "block", cursor: "pointer" }}
              >
                <Tooltip title={!drawerOpen ? item.text : ""} placement="right">
                  <ListItemButton
                    disabled={item.text === "Supplementary Material"}
                    component={item.text === "Settings" ? "div" : Link}
                    to={item.text === "Settings" ? "#" : item.path}
                    selected={isActive}
                    onClick={() => {
                      handleMenuClick(item.path);
                      if (item.text === "Settings") handleSettingsClick();
                    }}
                    sx={{
                      minHeight: 48,
                      justifyContent: drawerOpen ? "initial" : "center",
                      px: 2.5,
                      mb: 0.5,
                      borderRadius: "8px",
                      backgroundColor: isActive
                        ? theme.palette.secondary.main
                        : "transparent",
                      "&:hover": {
                        backgroundColor: isActive
                          ? theme.palette.secondary.main
                          : theme.palette.action.hover,
                      },
                      "&.Mui-selected": {
                        backgroundColor: theme.palette.secondary.main,
                        "&:hover": {
                          backgroundColor: theme.palette.secondary.main,
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: drawerOpen ? 2 : "auto",
                        justifyContent: "center",
                        color: isActive ? "#fff" : theme.palette.text.primary,
                      }}
                    >
                      {React.cloneElement(item.icon, {
                        fill: isActive ? "#fff" : "#00bfff",
                      })}
                    </ListItemIcon>
                    <ListItemText
                      secondary={item.text}
                      secondaryTypographyProps={{
                        fontWeight: isActive ? "700" : "500",
                        color: isActive ? "#fff" : theme.palette.text.primary,
                        variant: "body1",
                      }}
                      sx={{
                        opacity: drawerOpen ? 1 : 0,
                        transition: "all 0.2s ease",
                      }}
                    />
                    {drawerOpen && item.text === "Settings" && (
                      <Box>
                        <IconButton
                          sx={{
                            color: isActive
                              ? "#fff"
                              : theme.palette.secondary.main,
                          }}
                          onDoubleClick={() => setArrow(false)}
                          onClick={() => setArrow(true)}
                        >
                          <KeyboardDoubleArrowDownIcon />
                        </IconButton>
                      </Box>
                    )}
                  </ListItemButton>
                </Tooltip>

                {drawerOpen && item.text === "Settings" && arrow && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      color: theme.palette.secondary.main,
                      fontSize: "0.875rem",
                      fontWeight: "400",
                      backgroundColor: theme.palette.secondary.light,
                      borderRadius: "5px",
                      margin: "0 8px",
                    }}
                  >
                    <Typography
                      onClick={() => handleChangePassword()}
                      variant="subtitle2"
                      sx={{
                        minWidth: "30%",
                        maxWidth: "30%",
                        p: 2,
                        color: theme.palette.secondary.main,
                        cursor: "pointer",
                      }}
                    >
                      CHANGE PASSWORD
                    </Typography>
                  </Box>
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>
    </>
  );
};

const Header = ({ drawerOpen, setDrawerOpen }) => {
  const theme = useTheme();
  const { logoutUser } = useLogout();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const handleDrawerToggle = () => setDrawerOpen(!drawerOpen);

  const adminLogout = () => {
    logoutUser({
      navigation: () => navigate("/adminLogin"),
    });
  };

  return (
    <>
      <CssBaseline />
      <Drawer
        sx={{
          width: drawerOpen ? drawerWidth : collapsedWidth,
          flexShrink: 0,
          zIndex: 0,
          whiteSpace: "nowrap",
          "& .MuiDrawer-paper": {
            width: drawerOpen ? drawerWidth : collapsedWidth,
            transition: theme.transitions.create("width", {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: "hidden",
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            justifyContent: "space-between",
          },
        }}
        variant={isMobile ? "temporary" : "permanent"}
        open={drawerOpen}
        onClose={handleDrawerToggle}
      >
        <SidebarContent
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          theme={theme}
        />
        <Box sx={{ py: 3 }}>
          <Button
            fullWidth
            startIcon={<LogoutSvgIcon fill={"#b61547"} />}
            onClick={adminLogout}
            sx={{
              textTransform: "none",
              fontWeight: "bold",
              color: "#b61547",
              justifyContent: drawerOpen ? "flex-start" : "start",
              ":hover": { background: "#f7c0d2" },
            }}
          >
            {drawerOpen && "Logout"}
          </Button>
        </Box>
      </Drawer>

      {/* Persistent Drawer Toggle with custom styles */}
      <IconButton
        onClick={handleDrawerToggle}
        sx={{
          position: "fixed",
          top: "20px",
          left: drawerOpen
            ? `${drawerWidth - 20}px`
            : `${collapsedWidth - 20}px`,
          zIndex: 0,
          width: 30,
          height: 30,
          backgroundColor: theme.palette.secondary.main,
          color: "#fff",
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          border: "2px solid #fff",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          "&:hover": {
            backgroundColor: theme.palette.secondary.dark,
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          },
          "&:active": {
            transform: "scale(0.95)",
          },
          "& .MuiSvgIcon-root": {
            transition: "transform 0.3s ease",
            fontSize: "15px",
          },
        }}
      >
        {drawerOpen ? (
          <ChevronLeftIcon fontSize="small" />
        ) : (
          <ChevronRightIcon fontSize="small" />
        )}
      </IconButton>
    </>
  );
};

export default Header;
